import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers';
import { TimePicker } from 'antd';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { errorMessage, otcRegExp } from 'utils/constants';
import { getMinimumSelectableDate } from 'utils/Helpers';
import { OtcHelper } from '../../utils/helpers/otc.helper';
import AnimateButton from '../@extended/AnimateButton';
import LoadingButton from '../@extended/LoadingButton';
import { DeleteFilled, CheckCircleOutlined } from '@ant-design/icons';
import Autocomplete from '../common/Autocomplete';
import { Alert } from '../common/Alert';
import DisplayOtc from './DisplayOtc';
import { dayPickerOptions } from '../../utils/constants/day-picker';

const AddEditOtcModal = ({ otc, type, onCreateOrUpdate, onDelete, onClose }) => {
    const isEdit = type === 'edit';

    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [resultOtc, setResultOtc] = useState(null);

    const getMinDateTime = () => new Date();

    const getDefActivationDateTime = () => {
        const now = new Date();
        now.setSeconds(0);
        now.setMilliseconds(0);
        now.setMinutes(now.getMinutes() + 5);
        return now;
    };

    const getDefExpirationDateTime = activationDate => {
        const dateCopy = new Date(activationDate.getTime());
        dateCopy.setHours(dateCopy.getHours() + 1);
        return dateCopy;
    };

    const parseTimestamp = timestamp => {
        if (!timestamp) return new Date();

        const seconds = timestamp.seconds || timestamp._seconds || 0;
        const nanoseconds = timestamp.nanoseconds || timestamp._nanoseconds || 0;

        return new Date(seconds * 1000 + nanoseconds / 1000000);
    };

    const getInitialAccessDays = () => {
        if (!isEdit || !otc) return [];
        if (otc.access_days?.length) {
            return dayPickerOptions.filter(option =>
                otc.access_days.includes(option.value)
            );
        }
        if (otc.access_start_time && otc.access_end_time) {
            return [dayPickerOptions[0]]; // "Any Day"
        }
        return [];
    };

    const getInitialTimeRange = () => {
        if (!isEdit || !otc || !otc.access_start_time || !otc.access_end_time) {
            return null;
        }
        const anchor = dayjs(0);
        return [
            anchor
                .hour(otc.access_start_time.hours)
                .minute(otc.access_start_time.minutes),
            anchor
                .hour(otc.access_end_time.hours)
                .minute(otc.access_end_time.minutes)
        ];
    };

    const getInitialRestrictHours = () =>
        isEdit &&
        !!otc &&
        !!(otc.access_days?.length || (otc.access_start_time && otc.access_end_time));

    const minActivationDate =
        isEdit && otc
            ? getMinimumSelectableDate(parseTimestamp(otc.starts_at))
            : getMinDateTime();

    const validationSchema = Yup.object().shape({
        name: isEdit
            ? Yup.string()
            : Yup.string()
                  .trim()
                  .matches(otcRegExp.format, errorMessage.fullNameOrCompany.valid)
                  .required(errorMessage.fullNameOrCompany.required),
        activation_date: Yup.date()
            .required(errorMessage.toDate.required)
            .test(
                'activation_not_in_past',
                'Activation date cannot be in the past',
                value => !value || value.getTime() >= minActivationDate.getTime()
            ),
        expiration_date: Yup.date().when('reusable', {
            is: true,
            then: schema =>
                schema
                    .required(errorMessage.toDate.required)
                    .test(
                        'expiration_at_least_one_hour_after_activation',
                        'Expiration date must be at least 1 hour from activation date',
                        function (value) {
                            const { activation_date } = this.parent;
                            if (!activation_date || !value) return false;

                            const oneHourLater = new Date(activation_date);
                            oneHourLater.setHours(oneHourLater.getHours() + 1);

                            return value.getTime() >= oneHourLater.getTime();
                        }
                    )
        }),
        reusable: Yup.boolean(),
        restrict_hours: Yup.boolean(),
        access_days: Yup.array().when('restrict_hours', {
            is: true,
            then: schema => schema.min(1, errorMessage.accessDays.required),
            otherwise: schema => schema.notRequired()
        }),
        time_range: Yup.mixed()
            .when('restrict_hours', {
                is: true,
                then: schema => schema.required(errorMessage.timeRange.required),
                otherwise: schema => schema
            })
            .nullable()
    });

    const getInitialValues = () => {
        if (isEdit && otc) {
            const activationFromOtc = parseTimestamp(otc.starts_at);
            const expirationFromOtc = parseTimestamp(otc.expires_at);

            return {
                name: otc.name,
                reusable: otc?.reusable || false,
                activation_date: activationFromOtc,
                expiration_date: expirationFromOtc,
                restrict_hours: getInitialRestrictHours(),
                access_days: getInitialAccessDays(),
                time_range: getInitialTimeRange()
            };
        }

        const defaultActivation = getDefActivationDateTime();
        return {
            name: '',
            reusable: false,
            activation_date: defaultActivation,
            expiration_date: getDefExpirationDateTime(defaultActivation),
            restrict_hours: false,
            access_days: [],
            time_range: null
        };
    };

    const buildPayload = values => {
        const payload = {
            reusable: values.reusable
        };

        if (isEdit) {
            if (values.activation_date) {
                payload.starts_at = OtcHelper.toFirestoreTimestamp(
                    values.activation_date
                );
            }

            if (values.reusable) {
                payload.expires_at = OtcHelper.toFirestoreTimestamp(
                    values.expiration_date
                );
            } else {
                const startsAt = new Date(values.activation_date);
                const expiresAt = new Date(startsAt);
                expiresAt.setDate(startsAt.getDate() + 30);
                payload.expires_at = OtcHelper.toFirestoreTimestamp(
                    expiresAt.toISOString()
                );
            }
        } else {
            payload.name = values.name;

            if (values.activation_date) {
                payload.starts_at = OtcHelper.toFirestoreTimestamp(
                    values.activation_date
                );
            }

            if (values.reusable && values.expiration_date) {
                payload.expires_at = OtcHelper.toFirestoreTimestamp(
                    values.expiration_date
                );
            }
        }

        if (values.restrict_hours) {
            const isAnyDay = values.access_days?.some(day => day.value === '');
            payload.access_days = isAnyDay
                ? null
                : (values.access_days ?? []).map(day => day.value);
            payload.access_start_time = values.time_range
                ? {
                      hours: values.time_range[0].hour(),
                      minutes: values.time_range[0].minute()
                  }
                : null;
            payload.access_end_time = values.time_range
                ? {
                      hours: values.time_range[1].hour(),
                      minutes: values.time_range[1].minute()
                  }
                : null;
        } else {
            payload.access_days = null;
            payload.access_start_time = null;
            payload.access_end_time = null;
        }

        return payload;
    };

    const handleFormSubmit = async values => {
        const payload = buildPayload(values);

        setSubmitError(null);
        setSubmitting(true);
        try {
            const updated = await onCreateOrUpdate(payload, isEdit ? 'edit' : 'create');
            setResultOtc(updated || otc);
        } catch (error) {
            setSubmitError(
                error?.message ||
                    `Failed to ${isEdit ? 'update' : 'create'} the code. Please try again.`
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = () => {
        if (onDelete && otc) {
            onDelete(otc, onClose);
        }
    };

    const initialValues = useMemo(
        () => getInitialValues(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isEdit, otc?.code_id ?? otc?.id]
    );

    if (resultOtc) {
        return (
            <>
                <DialogTitle>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CheckCircleOutlined style={{ color: '#33c78f' }} />
                        <span>{isEdit ? 'Code Updated' : 'Code Generated'}</span>
                    </Stack>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ py: 2.5, px: 15, minHeight: 250 }}>
                    <DisplayOtc otc={resultOtc} />
                </DialogContent>
                <Divider />
                <DialogActions sx={{ p: 2.5, justifyContent: 'center' }}>
                    <AnimateButton>
                        <Button variant="contained" onClick={onClose}>
                            Done
                        </Button>
                    </AnimateButton>
                </DialogActions>
            </>
        );
    }

    return (
        <>
            <DialogTitle>
                {isEdit ? 'Edit One-Time Code' : 'Add One-Time Code'}
            </DialogTitle>
            <Divider />
            <Formik
                initialValues={initialValues}
                onSubmit={handleFormSubmit}
                validationSchema={validationSchema}
                validateOnChange
                enableReinitialize
            >
                {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    isValid,
                    setFieldValue,
                    touched,
                    values
                }) => {
                    const minExpirationDate = new Date(values.activation_date);
                    minExpirationDate.setHours(minExpirationDate.getHours() + 1);

                    return (
                        <>
                            <DialogContent sx={{ p: 3, minHeight: 280 }}>
                                <Stack spacing={2.5}>
                                    {submitError && (
                                        <Alert text={submitError} color="error" />
                                    )}

                                    {isEdit ? (
                                        <Stack spacing={1.5}>
                                            <Box>
                                                <Typography
                                                    variant="overline"
                                                    color="secondary"
                                                >
                                                    Code for
                                                </Typography>
                                                <Typography
                                                    variant="h3"
                                                    fontWeight={700}
                                                    component="div"
                                                    color="secondary"
                                                >
                                                    {otc.name}
                                                </Typography>
                                            </Box>
                                            <DisplayOtc otc={otc} compact />
                                        </Stack>
                                    ) : (
                                        <Stack spacing={0.75}>
                                            <InputLabel required>
                                                Full Name or Company Name
                                            </InputLabel>
                                            <TextField
                                                required
                                                id="name"
                                                name="name"
                                                placeholder="Full Name or Company Name"
                                                fullWidth
                                                autoComplete="given-name"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.name && !!errors.name}
                                                helperText={touched.name && errors.name}
                                                value={values.name}
                                            />
                                        </Stack>
                                    )}

                                    <Stack spacing={0.75}>
                                        <InputLabel required>
                                            Activation Date & Time
                                        </InputLabel>
                                        <FormControl
                                            sx={{ width: '100%' }}
                                            error={Boolean(
                                                touched.activation_date &&
                                                    errors.activation_date
                                            )}
                                        >
                                            <LocalizationProvider
                                                dateAdapter={AdapterDateFns}
                                            >
                                                <DateTimePicker
                                                    closeOnSelect={false}
                                                    value={values.activation_date}
                                                    onChange={newValue => {
                                                        setFieldValue(
                                                            'activation_date',
                                                            newValue
                                                        );

                                                        const newExpiration = new Date(
                                                            newValue
                                                        );
                                                        newExpiration.setHours(
                                                            newExpiration.getHours() + 1
                                                        );
                                                        setFieldValue(
                                                            'expiration_date',
                                                            newExpiration
                                                        );
                                                    }}
                                                    minutesStep={1}
                                                    minDateTime={minActivationDate}
                                                />
                                            </LocalizationProvider>
                                            {touched.activation_date &&
                                                errors.activation_date && (
                                                    <FormHelperText sx={{ ml: '14px' }}>
                                                        {errors.activation_date}
                                                    </FormHelperText>
                                                )}
                                        </FormControl>
                                    </Stack>

                                    <Box
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1.5,
                                            p: 2
                                        }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={values.reusable}
                                                    onChange={() =>
                                                        setFieldValue(
                                                            'reusable',
                                                            !values.reusable
                                                        )
                                                    }
                                                />
                                            }
                                            label={
                                                <Stack spacing={0.25} sx={{ py: 0.5 }}>
                                                    <Typography variant="subtitle2">
                                                        Reusable Code
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="secondary"
                                                    >
                                                        Allow this code to be used more
                                                        than once, until it expires.
                                                    </Typography>
                                                </Stack>
                                            }
                                            sx={{
                                                ml: 0,
                                                alignItems: 'flex-start',
                                                width: '100%'
                                            }}
                                        />

                                        {values.reusable && (
                                            <Stack spacing={0.75} sx={{ mt: 2, pl: 4 }}>
                                                <InputLabel required>
                                                    Expiration Date & Time (min. 1 hour
                                                    after activation)
                                                </InputLabel>
                                                <FormControl
                                                    sx={{ width: '100%' }}
                                                    error={Boolean(
                                                        touched.expiration_date &&
                                                            errors.expiration_date
                                                    )}
                                                >
                                                    <LocalizationProvider
                                                        dateAdapter={AdapterDateFns}
                                                    >
                                                        <DateTimePicker
                                                            closeOnSelect={false}
                                                            value={values.expiration_date}
                                                            onChange={newValue =>
                                                                setFieldValue(
                                                                    'expiration_date',
                                                                    newValue
                                                                )
                                                            }
                                                            minutesStep={1}
                                                            minDateTime={
                                                                minExpirationDate
                                                            }
                                                            disabled={!values.reusable}
                                                        />
                                                    </LocalizationProvider>
                                                    {touched.expiration_date &&
                                                        errors.expiration_date && (
                                                            <FormHelperText
                                                                sx={{ ml: '14px' }}
                                                            >
                                                                {errors.expiration_date}
                                                            </FormHelperText>
                                                        )}
                                                </FormControl>
                                            </Stack>
                                        )}
                                    </Box>

                                    <Box
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1.5,
                                            p: 2
                                        }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={values.restrict_hours}
                                                    onChange={() => {
                                                        const next =
                                                            !values.restrict_hours;
                                                        setFieldValue(
                                                            'restrict_hours',
                                                            next
                                                        );
                                                        if (!next) {
                                                            setFieldValue(
                                                                'access_days',
                                                                []
                                                            );
                                                            setFieldValue(
                                                                'time_range',
                                                                null
                                                            );
                                                        }
                                                    }}
                                                />
                                            }
                                            label={
                                                <Stack spacing={0.25} sx={{ py: 0.5 }}>
                                                    <Typography variant="subtitle2">
                                                        Restrict Access Days & Hours
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="secondary"
                                                    >
                                                        Limit when this code can be used
                                                        to specific days and times.
                                                    </Typography>
                                                </Stack>
                                            }
                                            sx={{
                                                ml: 0,
                                                alignItems: 'flex-start',
                                                width: '100%'
                                            }}
                                        />

                                        {values.restrict_hours && (
                                            <Stack spacing={2} sx={{ mt: 2, pl: 4 }}>
                                                <Stack spacing={0.75}>
                                                    <InputLabel>Access Days</InputLabel>
                                                    <FormControl
                                                        error
                                                        variant="standard"
                                                    >
                                                        <Autocomplete
                                                            multiple
                                                            id="access-days"
                                                            options={dayPickerOptions}
                                                            getOptionLabel={option =>
                                                                option.label
                                                            }
                                                            isOptionEqualToValue={(
                                                                option,
                                                                value
                                                            ) =>
                                                                option.value ===
                                                                value.value
                                                            }
                                                            value={
                                                                values.access_days ?? []
                                                            }
                                                            filterSelectedOptions
                                                            onChange={newValue => {
                                                                const last =
                                                                    newValue[
                                                                        newValue.length - 1
                                                                    ];
                                                                if (last?.value === '') {
                                                                    setFieldValue(
                                                                        'access_days',
                                                                        [last]
                                                                    );
                                                                } else {
                                                                    const cleaned =
                                                                        newValue.filter(
                                                                            v =>
                                                                                v.value !==
                                                                                ''
                                                                        );
                                                                    const deduped =
                                                                        Array.from(
                                                                            new Map(
                                                                                cleaned.map(
                                                                                    v => [
                                                                                        v.value,
                                                                                        v
                                                                                    ]
                                                                                )
                                                                            ).values()
                                                                        );
                                                                    // Keep chips in a
                                                                    // stable Mon…Sun
                                                                    // order regardless
                                                                    // of click order —
                                                                    // dayPickerOptions
                                                                    // is already
                                                                    // ordered that way.
                                                                    const sorted =
                                                                        deduped.sort(
                                                                            (a, b) =>
                                                                                dayPickerOptions.findIndex(
                                                                                    o =>
                                                                                        o.value ===
                                                                                        a.value
                                                                                ) -
                                                                                dayPickerOptions.findIndex(
                                                                                    o =>
                                                                                        o.value ===
                                                                                        b.value
                                                                                )
                                                                        );
                                                                    setFieldValue(
                                                                        'access_days',
                                                                        sorted
                                                                    );
                                                                }
                                                            }}
                                                            placeholder={
                                                                !values.access_days
                                                                    ?.length
                                                                    ? 'Select Access Days'
                                                                    : ''
                                                            }
                                                        />
                                                        {touched.access_days &&
                                                            !!errors.access_days && (
                                                                <FormHelperText>
                                                                    {errors.access_days}
                                                                </FormHelperText>
                                                            )}
                                                    </FormControl>
                                                </Stack>

                                                <Stack spacing={0.75}>
                                                    <InputLabel>Access Times</InputLabel>
                                                    <FormControl
                                                        error
                                                        variant="standard"
                                                    >
                                                        <TimePicker.RangePicker
                                                            popupStyle={{ zIndex: 1300 }}
                                                            order={false}
                                                            value={values.time_range}
                                                            use12Hours
                                                            id="time-range"
                                                            onChange={value =>
                                                                setFieldValue(
                                                                    'time_range',
                                                                    value
                                                                )
                                                            }
                                                            status={
                                                                touched.time_range &&
                                                                !!errors.time_range
                                                                    ? 'error'
                                                                    : ''
                                                            }
                                                            onBlur={handleBlur(
                                                                'time_range'
                                                            )}
                                                            format={'h:mm A'}
                                                            style={{ width: '100%' }}
                                                        />
                                                        {touched.time_range &&
                                                            !!errors.time_range && (
                                                                <FormHelperText>
                                                                    {errors.time_range}
                                                                </FormHelperText>
                                                            )}
                                                    </FormControl>
                                                </Stack>
                                            </Stack>
                                        )}
                                    </Box>

                                    <Alert
                                        color="warning"
                                        text={
                                            !values.reusable
                                                ? 'This code will expire automatically 30 days after its activation date. Some site restrictions may require SAGE Support to enable them.'
                                                : 'Some site restrictions may require SAGE Support to enable them.'
                                        }
                                    />
                                </Stack>
                            </DialogContent>
                            <Divider />
                            <DialogActions sx={{ p: 2.5 }}>
                                <Grid
                                    container
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Grid item>
                                        {isEdit && (
                                            <Tooltip title="Revoke Code" placement="top">
                                                <span>
                                                    <IconButton
                                                        size="large"
                                                        color="error"
                                                        onClick={handleDelete}
                                                        disabled={submitting}
                                                    >
                                                        <DeleteFilled />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </Grid>
                                    <Grid item>
                                        <Stack
                                            direction="row"
                                            spacing={2}
                                            alignItems="center"
                                        >
                                            <AnimateButton>
                                                <Button
                                                    color="error"
                                                    onClick={onClose}
                                                    disabled={submitting}
                                                >
                                                    Cancel
                                                </Button>
                                            </AnimateButton>
                                            <AnimateButton>
                                                <LoadingButton
                                                    type="submit"
                                                    variant="contained"
                                                    onClick={handleSubmit}
                                                    disabled={!isValid}
                                                    loading={submitting}
                                                >
                                                    {isEdit ? 'Update' : 'Generate Code'}
                                                </LoadingButton>
                                            </AnimateButton>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </DialogActions>
                        </>
                    );
                }}
            </Formik>
        </>
    );
};

export default AddEditOtcModal;
